/* eslint-disable @typescript-eslint/no-explicit-any */
import { showToastrMessage } from './Toastr.Utils';
import { rememberOrderDetailReturnPath } from './orderDetailNavigation.utils';
import type { ViewPort } from '@/common/context/ViewPort/ViewPort';
import { DESKTOP_VIEW_PORT } from '@/common/context/ViewPort/ViewPort';
import type { TooltipTrigger } from '@/components/utils/TypeUtils';

/**
 * Function to return the tooltip trigger based on the viewport
 * @param screen - ViewPort
 * @param defaultDesktopTrigger - Default Desktop Trigger
 * @param defaultMobileTrigger - Default Mobile Trigger
 */
export const returnToolTipTriggerByViewport = (
  screen: ViewPort,
  defaultDesktopTrigger: TooltipTrigger = 'hover',
  defaultMobileTrigger: TooltipTrigger = []
): TooltipTrigger => {
  if (DESKTOP_VIEW_PORT.includes(screen)) {
    return defaultDesktopTrigger;
  }
  return defaultMobileTrigger;
};

/** Method to check if a object has properties or not.
 *
 * Note: I am using any here as type due to the fact that the object will have diferent properties.
 */
export const hasProperties = (obj: any): boolean => Object.keys(obj).length > 0;

/**
 * Method to navigate the user to a details page.
 *
 * @param id - ID of the item to navigate to.
 * @param path - Path to navigate to with the ID.
 * @param errorMessage - Error message to display if navigation fails.
 */
export const navigateToDetailsPage = (
  id: string | number,
  path: string,
  errorMessage: string,
  navigateFunction: (url: string) => void
) => {
  if (id) {
    if (path === '/order') {
      rememberOrderDetailReturnPath();
    }

    navigateFunction(`${path}/${id}`);
  } else {
    showToastrMessage(errorMessage, 'failure');
  }
};

export const formatDecimalInNumber = (value: number): number => Number(value.toFixed(2));

/**
 * Function to capitalize the first letter of each word in a string
 * @param inputString - String to capitalize
 */
export const capitalizeWords = (inputString: string) => {
  const words = inputString.toLowerCase().split(' ');

  const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));

  const resultString = capitalizedWords.join(' ');

  return resultString;
};

/**
 * Function to return the element postion in DOM
 * @param element - HTML Element
 */
export function getRect(element: HTMLElement | null) {
  if (!element) {
    return {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
    };
  }

  return element.getBoundingClientRect();
}

/**
 * Function to check if the style class is mobile view
 * @param styleClass - Style class to check if it is mobile view
 */
export const isMobileView = (styleClass: string) => styleClass.includes('mobile');

/**
 * Function to check if the style class is desktop view
 * @param styleClass - Style class to check if it is desktop view
 */
export const isDesktopView = (styleClass: string) => styleClass.includes('desktop');

/**
 *
 * @returns unique number.
 */
export const generateUniqueNumber = () => {
  const timestamp = Date.now();
  const randomInt = Math.floor(Math.random() * 90000) + 10000;
  const uniqueNumber = `${timestamp}${randomInt}`;

  return parseInt(uniqueNumber, 10);
};

/**
 * Method to format the decimal places.
 *
 * @param value - Number of decimal places to format
 */
export const formatDecimal = (value: number) =>
  value.toFixed(2); /** Convert to fixed decimal format */

/**
 * Returns a string representation of the image extension.
 *
 * @param imageSrc - Image source
 */
export const getImageExtension = (imageSrc: string) => {
  const imageExtension = imageSrc.split('.').pop();

  return imageExtension;
};

export const getLatestRecordBasedOnField = <T>(arr: T[], field: keyof T): T | null => {
  if (!arr || arr.length === 0) {
    return null;
  }

  return arr.reduce((a: T, b: T) => {
    const aField = a[field];
    const bField = b[field];

    /** If the field is a string and can be parsed as a date, compare it as a date */
    if (typeof aField === 'string' && typeof bField === 'string') {
      const dateA = new Date(aField);
      const dateB = new Date(bField);

      /** Check if both values are valid dates */
      if (!Number.isNaN(dateA.getTime()) && !Number.isNaN(dateB.getTime())) {
        return dateA > dateB ? a : b;
      }
    }

    /** Fall back to generic comparison for non-date fields */
    return aField > bField ? a : b;
  }, arr[0]); // Set the initial value as the first element of the array
};
